<template>
  <div
    ref="root"
    class="views-panel"
    :class="{
      'views-panel--collapsed': collapsed,
      'views-panel--overlay': overlay,
    }"
    :role="overlay ? 'dialog' : undefined"
    :aria-modal="overlay ? 'true' : undefined"
    :aria-label="overlay ? $t('viewsPanel.title') : undefined"
    @keydown="onKeydown"
  >
    <!-- Shared with the sidebar's own head (`Sidebar.vue`) rather than
         hand-building the bar again: `PanelHeader` is what keeps this panel's
         51px band, border and title typography from drifting away from the
         sidebar's identical bar next to it -- the two used to agree only by
         both authors copying the same numbers. -->
    <PanelHeader class="views-panel__head">
      <h2 v-if="!collapsed" class="panel-header__title views-panel__title">
        {{ $t('viewsPanel.title') }}
      </h2>
      <!-- When the rail is a rail only because the sidebar holds the left
           navigation budget (Model C), the plain "Show views panel" tooltip
           hides the one thing the user needs to know: expanding this will send
           the sidebar to a rail. Saying so turns a panel that appears to have
           collapsed itself into a visible trade the user is making. -->
      <button
        v-show="toggleActionable"
        v-tooltip="toggleTooltip"
        type="button"
        class="views-panel__toggle"
        :aria-label="toggleLabel"
        :aria-expanded="!collapsed"
        aria-controls="views-panel-list"
        @click="$emit('update:collapsed', !collapsed)"
      >
        <i class="baserow-icon-views-panel" aria-hidden="true"></i>
      </button>
    </PanelHeader>

    <!-- Always present, not gated on list length. It used to appear only past
         eight views, on the reasoning that shorter lists are faster to scan
         than to type into -- but a control that comes and goes as views are
         added is harder to rely on than one that is simply always there. -->
    <div v-if="!collapsed" class="views-panel__search">
      <i class="views-panel__search-icon iconoir-search" aria-hidden="true"></i>
      <input
        v-model="query"
        type="search"
        class="views-panel__search-input"
        :aria-label="$t('viewsPanel.searchView')"
        :placeholder="$t('viewsPanel.searchView')"
      />
    </div>

    <div
      v-if="!collapsed && isLoading"
      class="views-panel__loading"
      role="status"
      :aria-label="$t('viewsPanel.loading')"
    >
      <div class="loading"></div>
    </div>

    <!-- One list, two chromes. Collapsed and expanded render from the same
         `viewGroups` computed, so "which views, in what order, which is
         active" has exactly one definition. Collapsed drops the name to a
         tooltip because the rail is too narrow for it, and drops the group
         labels because a 48px column has no room for a heading -- the
         dividers carry the grouping instead. -->
    <div
      v-else
      v-auto-overflow-scroll
      class="views-panel__body"
      :class="{ 'views-panel__body--collapsed': collapsed }"
    >
      <ul
        id="views-panel-list"
        ref="tablist"
        class="views-panel__list"
        role="tablist"
        aria-orientation="vertical"
        :aria-label="$t('viewsPanel.title')"
        @keydown.down.prevent="moveFocus(1)"
        @keydown.up.prevent="moveFocus(-1)"
        @keydown.home.prevent="focusAt(0)"
        @keydown.end.prevent="focusAt(flatViews.length - 1)"
      >
        <li
          v-for="(group, groupIndex) in viewGroups"
          :key="group.type + 'group'"
          class="views-panel__group"
        >
          <div
            v-if="!collapsed"
            :id="'views-group-' + group.type"
            class="views-panel__group-label"
          >
            {{ group.name }}
          </div>
          <!-- Collapsed has no headings, so a hairline stands in for the
               group boundary. Never on the first group -- and never above a
               group that renders nothing, or the hairline is left floating
               with no rows above it to separate from. A group stays in
               `viewGroups` on an empty folder alone (so the folder itself is
               still reachable to rename or delete), but the collapsed rail
               shows folder *contents*, not folder chrome, so an all-empty
               group like that draws zero rows here -- measured live: a
               "Restricted" group holding only two empty folders put the
               divider meant for "Collaborative" at the very top of the rail
               instead, above nothing. -->
          <div
            v-else-if="
              collapsed && groupIndex > 0 && precedingGroupsHaveRows(groupIndex)
            "
            class="views-panel__group-divider"
            aria-hidden="true"
          ></div>

          <!-- Folders come before the loose views, so the table's named
               structure reads first and the ungrouped remainder falls to the
               bottom. Collapsed drops the folder chrome entirely: a 48px column
               has no room for a disclosure row, so the folders' views are shown
               inline, in order, with a hairline between folders. -->
          <!-- The folders get their own container rather than sitting as bare
               siblings of the group label and the loose-view list. `v-sortable`
               derives the order from `el.parentNode.childNodes`, so any
               non-sortable element sharing that parent is read as a list member
               with an `undefined` id. With the folders directly under the group
               `<li>`, every reorder posted `view_folder_ids` containing nulls
               (a 400 from the integer-list serializer) and shifted the local
               order by the number of phantom nodes. -->
          <div v-if="!collapsed && group.folders.length > 0">
            <div
              v-for="folder in group.folders"
              :key="'folder-' + folder.id"
              v-sortable="{
                enabled: canOrderFolders,
                id: folder.id,
                update: createFolderOrderCall(group.type),
                marginTop: -1,
                handle: '[data-sortable-folder-handle]',
              }"
              class="views-panel__folder"
            >
              <div class="views-panel__folder-head">
                <button
                  type="button"
                  class="views-panel__folder-toggle"
                  :aria-expanded="isFolderOpen(folder) ? 'true' : 'false'"
                  :aria-controls="'views-folder-' + folder.id"
                  @click="toggleFolder(folder)"
                >
                  <i
                    class="views-panel__folder-chevron"
                    :class="
                      isFolderOpen(folder)
                        ? 'baserow-icon-folder-open views-panel__folder-chevron--open'
                        : 'iconoir-folder'
                    "
                    aria-hidden="true"
                  ></i>
                  <Editable
                    :ref="'folder-name-' + folder.id"
                    class="views-panel__folder-name"
                    :value="folder.name"
                    @change="renameFolder(folder, $event)"
                  ></Editable>
                  <span
                    v-if="folder.views.length > 0"
                    class="views-panel__folder-count"
                    >{{ folder.views.length }}</span
                  >
                </button>
                <!-- `iconoir-drag`'s actual glyph is four corner-arrows
                     converging on the centre -- a compress/focus icon, not a
                     grip -- so this uses the custom `baserow-icon-drag-handle`
                     glyph (a real six-dot grip, `assets/icons/drag-handle.svg`)
                     instead. -->
                <span
                  v-if="canOrderFolders"
                  class="views-panel__folder-handle"
                  data-sortable-folder-handle
                  role="button"
                  tabindex="-1"
                  :aria-label="$t('viewsPanel.reorderFolder')"
                  @click.stop
                >
                  <i
                    class="baserow-icon-drag-handle views-panel__folder-handle-icon"
                    aria-hidden="true"
                  ></i>
                </span>
                <button
                  v-if="!readOnly"
                  :ref="'folder-options-' + folder.id"
                  type="button"
                  class="views-panel__folder-options"
                  :aria-label="$t('viewsPanel.folderOptions')"
                  aria-haspopup="menu"
                  @click="toggleFolderContext(folder, $event)"
                >
                  <i class="baserow-icon-more-vertical" aria-hidden="true"></i>
                </button>
              </div>
              <ul
                v-show="isFolderOpen(folder)"
                :id="'views-folder-' + folder.id"
                class="views-panel__group-list views-panel__folder-list"
                :data-sortable-container-id="'folder-' + folder.id"
              >
                <ViewsContextItem
                  v-for="view in folder.views"
                  :ref="'view-' + view.id"
                  :key="view.id"
                  v-sortable="{
                    enabled: canOrder,
                    id: view.id,
                    update: createOrderCall(group.type),
                    marginTop: -1,
                    handle: '[data-sortable-handle]',
                    containerId: 'folder-' + folder.id,
                    onMoveToContainer: moveViewToContainer,
                  }"
                  :database="database"
                  :view="view"
                  :table="table"
                  :read-only="readOnly"
                  :store-prefix="storePrefix"
                  tab
                  :drag-handle="canOrder"
                  @selected="selectedView"
                ></ViewsContextItem>
                <!-- An open folder with nothing in it would otherwise render
                     its whole disclosure row -- chevron, name, count, menu --
                     over an empty box, so the chrome is all there is to see
                     and the folder reads as broken rather than as empty.

                     `folder.views` is the SEARCH-FILTERED list, so during a
                     search an empty one means "nothing here matches", not
                     "this folder is empty" -- and the folder is force-opened
                     while searching, so the wrong message would be the thing
                     the user sees most. -->
                <li
                  v-if="folder.views.length === 0"
                  class="views-panel__folder-empty"
                >
                  {{
                    query
                      ? $t('viewsPanel.emptyFolderSearch')
                      : $t('viewsPanel.emptyFolder')
                  }}
                </li>
              </ul>
            </div>
          </div>

          <ul
            class="views-panel__group-list"
            data-sortable-container-id="loose"
            :aria-labelledby="
              !collapsed ? 'views-group-' + group.type : undefined
            "
          >
            <ViewsContextItem
              v-for="view in collapsed ? group.views : group.looseViews"
              :ref="'view-' + view.id"
              :key="view.id"
              v-sortable="{
                enabled: canOrder,
                id: view.id,
                update: createOrderCall(group.type),
                marginTop: -1,
                handle: '[data-sortable-handle]',
                containerId: 'loose',
                onMoveToContainer: moveViewToContainer,
              }"
              :database="database"
              :view="view"
              :table="table"
              :read-only="readOnly"
              :store-prefix="storePrefix"
              tab
              :name-tooltip="collapsed"
              :drag-handle="canOrder && !collapsed"
              @selected="selectedView"
            ></ViewsContextItem>
          </ul>
        </li>
      </ul>

      <div v-if="!collapsed" aria-live="polite">
        <div v-if="views.length === 0" class="views-panel__empty">
          <i
            class="views-panel__empty-icon iconoir-view-grid"
            aria-hidden="true"
          ></i>
          <span class="views-panel__empty-text">{{
            $t('viewsPanel.noViews')
          }}</span>
        </div>
        <!-- Keyed off the matched views rather than off `flatViews`, which
             now excludes the contents of collapsed folders: collapsing every
             folder is not the same as matching nothing. The icon matches the
             search field's own glyph above rather than the generic "no
             views" one, so the empty state visibly answers "your search",
             not "this table". -->
        <div
          v-else-if="matchedViews.length === 0"
          class="views-panel__empty views-panel__empty--search"
        >
          <i
            class="views-panel__empty-icon iconoir-search"
            aria-hidden="true"
          ></i>
          <span class="views-panel__empty-text">{{
            $t('viewsPanel.noSearchResults')
          }}</span>
        </div>
      </div>
    </div>

    <!-- Its own section between the list and the create rows. A folder
         reorganises views that already exist rather than adding another one, so
         it belongs to neither neighbour and is divided from both.

         It wears CreateViewLink's classes because it is the same shape of
         control -- an icon, a label, a trailing `+` -- and re-describing that
         in near-identical tokens is what made the two drift apart before.

         One button, always, regardless of how many ownership groups the table
         has. A folder is scoped to one ownership type -- the backend rejects a
         view/folder mismatch with ERROR_VIEW_FOLDER_OWNERSHIP_TYPE_MISMATCH --
         but that is a question to ask when there is more than one answer, not a
         reason to grow a second button per group. `newFolderOwnershipTypes`
         holds the choice: one entry creates directly, several open the menu. -->
    <div
      v-if="!collapsed && !readOnly && canCreateFolder"
      class="views-panel__section views-panel__section--folder"
    >
      <a
        ref="newFolderButton"
        class="select__footer-create-link"
        :aria-haspopup="newFolderOwnershipTypes.length > 1 ? 'menu' : undefined"
        @click="startCreateFolder"
      >
        <i
          class="select__footer-create-icon color-neutral baserow-icon-new-folder"
        ></i>
        <span class="select__footer-create-link-name">{{
          $t('viewsPanel.newFolder')
        }}</span>
        <i class="select__footer-create-link-icon iconoir-plus"></i>
      </a>
    </div>

    <!-- Only mounted when there is actually a choice to make. -->
    <Context
      v-if="newFolderOwnershipTypes.length > 1"
      ref="newFolderContext"
      overflow-scroll
      max-height-if-outside-viewport
    >
      <ul class="context__menu">
        <li
          v-for="ownershipType in newFolderOwnershipTypes"
          :key="ownershipType.getType()"
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link"
            @click="createFolderIn(ownershipType.getType())"
          >
            <i class="context__menu-item-icon iconoir-add-folder"></i>
            {{
              $t('viewsPanel.newFolderIn', { group: ownershipType.getName() })
            }}
          </a>
        </li>
      </ul>
    </Context>

    <!-- The create surface sits at the foot of the rail, below the list it adds
         to, and shows every option outright: one row per view type. No trigger
         to open first -- what you can create is what you can see. -->
    <div
      v-if="!collapsed && !readOnly && canCreate"
      class="views-panel__section views-panel__section--create"
    >
      <CreateViewLink
        v-for="(viewType, type) in viewTypes"
        :key="type"
        :database="database"
        :table="table"
        :view-type="viewType"
        @created="createdView"
      ></CreateViewLink>
    </div>

    <!-- One folder menu for the whole rail rather than one per row: it is
         anchored to whichever folder's trigger was clicked, so N folders do not
         mount N identical popovers. -->
    <ViewFolderContext
      ref="folderContext"
      :folder="contextFolder"
      :table="table"
      @rename="enableFolderRename"
    ></ViewFolderContext>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import { escapeRegExp } from '@baserow/modules/core/utils/string'
import {
  focusFirstElement,
  handleDialogTabKey,
  restoreFocus,
} from '@baserow/modules/core/utils/dialogFocus'
import {
  readLocalStorage,
  writeLocalStorage,
} from '@baserow/modules/core/utils/localStorage'
import Editable from '@baserow/modules/core/components/Editable'
import PanelHeader from '@baserow/modules/core/components/PanelHeader'
import ViewsContextItem from '@baserow/modules/database/components/view/ViewsContextItem'
import ViewFolderContext from '@baserow/modules/database/components/view/ViewFolderContext'
import CreateViewLink from '@baserow/modules/database/components/view/CreateViewLink'
import { mergeSublistOrder } from '@baserow/modules/database/utils/viewOrder'

/**
 * Which folders the user has collapsed, persisted per table. Collapsing a
 * folder is a statement about that table's views, not about the browser, so it
 * is keyed by table id -- unlike the rail's own collapsed state, which is a
 * preference about screen space and is stored globally.
 *
 * Only the *collapsed* ids are stored, so a folder created on another device
 * defaults to open rather than to invisible.
 */
const FOLDER_COLLAPSED_KEY_PREFIX = 'baserow.views.foldersCollapsed.'

/**
 * The always-visible views rail. It renders the same list the views dropdown
 * used to show, so the search, ownership grouping and drag-to-reorder behaviour
 * are deliberately identical to ViewsContext -- only the chrome differs.
 */
export default {
  name: 'ViewsPanel',
  components: {
    Editable,
    PanelHeader,
    ViewsContextItem,
    ViewFolderContext,
    CreateViewLink,
  },
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    views: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: false,
      default: false,
    },
    // True when the viewport is too narrow to give the rail its own column, in
    // which case it floats over the grid instead of pushing it aside.
    overlay: {
      type: Boolean,
      required: false,
      default: false,
    },
    // True when the rail is collapsed because the sidebar currently holds the
    // left navigation budget, rather than because the user collapsed it. Only
    // used to explain the state in the toggle's label; the rail's behaviour is
    // identical either way.
    forcedBySidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
    // False in `[sm, md)`, where the viewport forces the rail and the toggle
    // could only lie about what it does -- the same reasoning as `Sidebar.vue`'s
    // prop of the same name. Measured live: rendered, focusable, `aria-expanded`
    // present, and a click did nothing, because nothing here ever gated it on
    // the band the way the sidebar's toggle is.
    toggleActionable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['selected-view', 'update:collapsed'],
  data() {
    return {
      query: '',
      // Ids of the folders the user has collapsed, hydrated from localStorage.
      collapsedFolderIds: [],
      // The folder the shared folder menu is currently anchored to.
      contextFolder: null,
      // Where focus was when the overlay opened, so closing can put it back.
      returnFocusTo: null,
    }
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.view.loading,
      folders: (state) => state.view.folders,
    }),
    viewTypes() {
      return this.$registry.getAll('view')
    },
    viewOwnershipTypes() {
      return this.$registry.getAll('viewOwnershipType')
    },
    availableViewOwnershipTypesForCreation() {
      return this.activeViewOwnershipTypes.filter((t) =>
        t.userCanTryCreate(this.table, this.database.workspace.id)
      )
    },
    activeViewOwnershipTypes() {
      return Object.values(this.viewOwnershipTypes).sort(
        (a, b) => a.getListViewTypeSort() - b.getListViewTypeSort()
      )
    },
    canCreate() {
      return this.availableViewOwnershipTypesForCreation.length > 0
    },
    canOrder() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.order_views',
          this.table,
          this.database.workspace.id
        )
      )
    },
    /**
     * The single source of truth for the list: which views, in what order, and
     * grouped by ownership. Both the expanded list and the collapsed rail
     * render from this, so the two chromes cannot drift apart.
     *
     * Each ownership group additionally carries its folders, each with the
     * views inside it, and `looseViews` -- the top-level remainder. `views`
     * stays the flat, ordered list of everything in the group, which is what
     * the collapsed rail and the keyboard traversal use.
     */
    viewGroups() {
      return this.activeViewOwnershipTypes
        .map((type) => {
          const ownershipType = type.getType()
          const views = this.viewsByOwnership(this.views, ownershipType)
          const folders = this.folders
            .filter((folder) => folder.ownership_type === ownershipType)
            .map((folder) => ({
              ...folder,
              views: views.filter((view) => view.folder_id === folder.id),
            }))
            .sort((a, b) => a.order - b.order)

          return {
            type: ownershipType,
            name: type.getName(),
            views,
            folders,
            looseViews: views.filter((view) => !view.folder_id),
          }
        })
        .filter((group) => group.views.length > 0 || group.folders.length > 0)
    },
    canCreateFolder() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_view_folder',
          this.table,
          this.database.workspace.id
        )
      )
    },
    /**
     * The ownership types "New folder" can target. One entry means the button
     * creates directly; several mean it opens a menu to ask which.
     *
     * A folder's ownership type can never be inferred -- the backend rejects a
     * view/folder mismatch with ERROR_VIEW_FOLDER_OWNERSHIP_TYPE_MISMATCH -- so
     * with more than one group the user has to say. Asking is a menu, not a
     * second button per group.
     */
    newFolderOwnershipTypes() {
      return this.canCreateFolder
        ? this.availableViewOwnershipTypesForCreation
        : []
    },
    canOrderFolders() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.order_view_folders',
          this.table,
          this.database.workspace.id
        )
      )
    },
    /**
     * The groups flattened back into render order. This is what the arrow keys
     * traverse, so focus moves across a group boundary exactly the way the eye
     * does.
     *
     * Expanded, that order is folders first and the loose views after, and it
     * skips the contents of a collapsed folder -- a row that is not on screen
     * must not take focus, or arrowing appears to stall on nothing. Collapsed,
     * the rail renders one flat list, so the group's own order is the order.
     */
    flatViews() {
      return this.viewGroups.flatMap((group) => {
        if (this.collapsed) {
          return group.views
        }
        const inFolders = group.folders.flatMap((folder) =>
          this.isFolderOpen(folder) ? folder.views : []
        )
        return [...inFolders, ...group.looseViews]
      })
    },
    /**
     * Every view that survives the search box, regardless of which folder it
     * is in or whether that folder is open. This is what "did the search match
     * anything?" means.
     */
    matchedViews() {
      return this.viewGroups.flatMap((group) => group.views)
    },
    /**
     * The accessible name of the toggle. Plain collapse/expand, except while
     * the sidebar is holding the navigation budget, when expanding this panel
     * also sends the sidebar to a rail -- a consequence the control has to
     * announce rather than spring on the user.
     */
    toggleLabel() {
      if (!this.collapsed) {
        return this.$t('viewsPanel.collapse')
      }
      return this.forcedBySidebar
        ? this.$t('viewsPanel.expandOverSidebar')
        : this.$t('viewsPanel.expand')
    },
    toggleTooltip() {
      return this.toggleLabel
    },
  },
  watch: {
    // The rail is reused across tables, so the collapsed set has to follow the
    // table rather than being read once on mount.
    'table.id': {
      immediate: true,
      handler() {
        this.collapsedFolderIds = this.readCollapsedFolders()
      },
    },
    /**
     * Overlaying the grid makes this a modal surface, so opening it moves focus
     * in and closing it puts focus back where it came from. Without the first, a
     * keyboard user who opened the rail is still somewhere in the grid behind
     * the scrim; without the second, dismissing it drops focus to the document.
     *
     * The sidebar has done this since `05-sidebar-system.md` §3.3, which asks
     * for it of both panels; this one only ever got the scrim and Escape.
     */
    overlay(isOverlay) {
      if (isOverlay) {
        this.returnFocusTo =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        this.$nextTick(() => focusFirstElement(this.$refs.root))
      } else {
        restoreFocus(this.$refs.root, this.returnFocusTo)
        this.returnFocusTo = null
      }
    },
    /**
     * A route change dismisses the overlay, the same way the sidebar's does.
     * Picking a view is a `router.push`, and it is the thing this rail is
     * *for*: leaving it floating over the view the user just asked to see
     * makes every switch take two gestures, and the second one is a dismiss
     * they did not ask for. Docked, there is nothing to dismiss.
     */
    $route() {
      if (this.overlay) {
        this.$emit('update:collapsed', true)
      }
    },
  },
  methods: {
    selectedView(view) {
      this.$emit('selected-view', view)
    },
    /**
     * Whether any group before `groupIndex` actually draws a row in the
     * collapsed rail. `viewGroups` keeps a group that holds only empty
     * folders (so the folder stays reachable to rename or delete), but the
     * collapsed rail shows folder *contents*, not folder chrome -- an
     * all-empty group draws nothing there, so the divider meant to follow it
     * has to look past it for the last group that actually left something
     * above the line.
     */
    precedingGroupsHaveRows(groupIndex) {
      return this.viewGroups
        .slice(0, groupIndex)
        .some((group) => group.views.length > 0)
    },
    createdView(view) {
      this.selectedView(view)
    },
    /**
     * While overlaying the grid the rail covers content, so escape has to be
     * able to dismiss it. When it owns a column there is nothing to dismiss.
     */
    onEscape() {
      if (this.overlay) {
        this.$emit('update:collapsed', true)
      }
    },
    /**
     * Escape dismisses; Tab is trapped while overlaying, so a keyboard user
     * cannot walk out of the rail into the grid it is covering. Docked, the
     * panel is part of the page and Tab has to leave it normally, which is why
     * `handleDialogTabKey` is only reached in the overlay state.
     */
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.onEscape()
        return
      }
      if (this.overlay) {
        handleDialogTabKey(event, this.$refs.root)
      }
    },
    /**
     * Manual activation: arrowing moves focus only. Switching a view refetches
     * rows, fields, filters and sorts, so following focus with selection would
     * fire one load per keypress. Enter/Space on the focused row selects.
     */
    moveFocus(delta) {
      const views = this.flatViews
      if (views.length === 0) {
        return
      }
      // Arrowing before anything is focused starts from the selected row, and
      // failing that from the first one, so the first keypress always lands on
      // a real row rather than wrapping off the end.
      let from = views.findIndex((view) => this.isFocused(view))
      if (from === -1) {
        from = views.findIndex((view) => view._.selected)
      }
      if (from === -1) {
        this.focusAt(0)
        return
      }
      this.focusAt((from + delta + views.length) % views.length)
    },
    focusAt(index) {
      const view = this.flatViews[index]
      if (view === undefined) {
        return
      }
      const row = this.$refs['view-' + view.id]
      const component = Array.isArray(row) ? row[0] : row
      component?.focus()
    },
    isFocused(view) {
      const row = this.$refs['view-' + view.id]
      const component = Array.isArray(row) ? row[0] : row
      return component?.$el?.contains(document.activeElement) ?? false
    },
    searchAndOrder(views) {
      // Trimmed, because a pasted or autocompleted query routinely carries a
      // leading space and " Grid" would then match nothing while the input
      // looks, to the user, like it says "Grid". The icon picker's search
      // already normalises the same way.
      const query = this.query.trim()

      return views
        .filter(function (view) {
          const regex = new RegExp('(' + escapeRegExp(query) + ')', 'i')
          return view.name.match(regex)
        })
        .sort((a, b) => a.order - b.order)
    },
    viewsByOwnership(views, ownershipType) {
      return this.searchAndOrder(views).filter(
        (view) => view.ownership_type === ownershipType
      )
    },
    async order(ownershipType, order, oldOrder) {
      try {
        await this.$store.dispatch('view/order', {
          table: this.table,
          ownershipType,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    /**
     * `v-sortable` reports only the list the drag happened in -- one folder's
     * contents, or the loose views -- but `view/order` is a statement about the
     * whole ownership group. See `utils/viewOrder` for why posting the bare
     * sublist corrupted every other view's order.
     */
    toFullOrder(ownershipType, sublistOrder) {
      return mergeSublistOrder(
        this.viewsByOwnership(this.views, ownershipType).map((view) => view.id),
        sublistOrder
      )
    },
    createOrderCall(ownershipType) {
      return (order, oldOrder, ...rest) => {
        return this.order(
          ownershipType,
          this.toFullOrder(ownershipType, order),
          this.toFullOrder(ownershipType, oldOrder),
          ...rest
        )
      }
    },
    /**
     * `v-sortable`'s cross-list drop handler for the views rail: called when a
     * view is dragged from its own folder (or the loose-views list) into a
     * *different* one. `targetContainerId` is whatever was set as that list's
     * `data-sortable-container-id` -- `'folder-' + folder.id`, or `'loose'`.
     *
     * This reuses `view/moveViewToFolder`, the same action the "Move to
     * folder" menu already dispatches (`ViewMoveToFolderContext.vue`), so drag
     * and menu are two entry points into one code path rather than two
     * competing ones. Only membership changes here -- the target list's order
     * is whatever `moveViewToFolder` leaves it at, matching the menu's
     * behaviour, which never re-orders either.
     */
    async moveViewToContainer(viewId, targetContainerId) {
      const view = this.views.find((v) => v.id === viewId)
      if (!view) {
        return
      }
      const folder =
        targetContainerId === 'loose'
          ? null
          : this.folders.find((f) => 'folder-' + f.id === targetContainerId) ||
            null

      try {
        await this.$store.dispatch('view/moveViewToFolder', { view, folder })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    /**
     * While a search is running every folder is forced open, so a matching view
     * is never hidden behind a folder the user collapsed earlier. The stored
     * collapsed set is untouched, so clearing the search restores it.
     */
    isFolderOpen(folder) {
      if (this.query !== '') {
        return true
      }
      return !this.collapsedFolderIds.includes(folder.id)
    },
    /**
     * Persisted immediately rather than on unload: the rail is often left by
     * navigating away, and an unload handler would lose the last toggle.
     */
    toggleFolder(folder) {
      // While searching every folder is forced open, so the chevron reports a
      // state the stored set does not own. Toggling then would silently rewrite
      // the user's real preference, so it is ignored.
      if (this.query !== '') {
        return
      }
      const collapsed = this.collapsedFolderIds.includes(folder.id)
      this.collapsedFolderIds = collapsed
        ? this.collapsedFolderIds.filter((id) => id !== folder.id)
        : [...this.collapsedFolderIds, folder.id]
      this.persistCollapsedFolders()
    },
    folderStorageKey() {
      return FOLDER_COLLAPSED_KEY_PREFIX + this.table.id
    },
    readCollapsedFolders() {
      const raw = readLocalStorage(this.folderStorageKey(), '')
      if (!raw) {
        return []
      }
      // Stored as a comma-separated id list rather than JSON so a corrupted
      // value degrades to "nothing collapsed" instead of throwing.
      return raw
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    },
    persistCollapsedFolders() {
      writeLocalStorage(
        this.folderStorageKey(),
        this.collapsedFolderIds.join(',')
      )
    },
    toggleFolderContext(folder, event) {
      this.contextFolder = folder
      const target = this.$refs['folder-options-' + folder.id]
      const element = Array.isArray(target) ? target[0] : target
      this.$refs.folderContext.toggle(
        element || event.currentTarget,
        'bottom',
        'right',
        0
      )
    },
    /**
     * `ownershipType` is the caller's group, not a guess: a folder can only
     * hold views of its own ownership type, and the button that calls this
     * lives inside that group's own section now, so there is no default left
     * to get wrong. It used to fall back to `activeViewOwnershipTypes[0]`, a
     * fixed sort order rather than a scope -- `restricted` sorts first
     * whenever RBAC is active, so every folder created from the one shared
     * button landed there regardless of which group the user meant, and
     * collaborative views -- the common case -- could never get a
     * same-type folder to be organised into.
     */
    /**
     * Entry point for the "New folder" button. With a single ownership type
     * there is nothing to ask, so it creates; with several it opens the menu.
     */
    startCreateFolder() {
      const types = this.newFolderOwnershipTypes

      if (types.length === 1) {
        this.createFolder(types[0].getType())
        return
      }

      if (types.length > 1) {
        this.$refs.newFolderContext.toggle(
          this.$refs.newFolderButton,
          'top',
          'left',
          4
        )
      }
    },
    createFolderIn(ownershipType) {
      this.$refs.newFolderContext.hide()
      this.createFolder(ownershipType)
    },
    async createFolder(ownershipType) {
      try {
        await this.$store.dispatch('view/createFolder', {
          table: this.table,
          values: {
            name: this.$t('viewsPanel.newFolderName'),
            ownershipType,
          },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    /**
     * A folder is renamed in place in the rail, the same way a view is, rather
     * than in a modal -- the name is already rendered there and editing it
     * where it lives needs no context switch.
     */
    enableFolderRename(folder) {
      if (folder === null) {
        return
      }
      // The folder has to be open for its name to be reachable while typing.
      if (!this.isFolderOpen(folder)) {
        this.toggleFolder(folder)
      }
      this.$nextTick(() => {
        const target = this.$refs['folder-name-' + folder.id]
        const component = Array.isArray(target) ? target[0] : target
        component?.edit()
      })
    },
    async renameFolder(folder, event) {
      if (event.value === folder.name) {
        return
      }
      try {
        await this.$store.dispatch('view/updateFolder', {
          folder,
          values: { name: event.value },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async orderFolders(ownershipType, order, oldOrder) {
      try {
        await this.$store.dispatch('view/orderFolders', {
          table: this.table,
          ownershipType,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    createFolderOrderCall(ownershipType) {
      return (...lastArgs) => {
        return this.orderFolders(ownershipType, ...lastArgs)
      }
    },
  },
}
</script>
